import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, get, update, onValue } from 'firebase/database';
import './Pages.css';

const Profile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  
  // State for universal message
  const [universalMessage, setUniversalMessage] = useState('');
  
  // State for user orders
  const [userOrders, setUserOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersTotal, setOrdersTotal] = useState(0);
  
  // NEW: Dropdown state
  const [ordersExpanded, setOrdersExpanded] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      const user = localStorage.getItem('currentUser');
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const parsedUser = JSON.parse(user);
        if (parsedUser.isLoggedIn && parsedUser.userId) {
          const db = getDatabase();
          const snapshot = await get(ref(db, `users/${parsedUser.userId}`));
          if (snapshot.exists()) {
            const data = snapshot.val();
            setUserData({ ...data, userId: parsedUser.userId });
            setEditData({
              state: data.state || '',
              city: data.city || '',
              pin: data.pin || '',
              address: data.address || '',
              industryArea: data.industryArea || '',
              experience: data.experience || '',
              preferredLocation: data.preferredLocation || ''
            });
          } else {
            setUserData(parsedUser);
          }
        } else {
          navigate('/login');
        }
      } catch (e) {
        setError('Error loading profile data');
        console.error('Profile error:', e);
      }
      setLoading(false);
    };

    fetchUserData();
  }, [navigate]);

  // Listen for real-time updates to custom message from admin
  useEffect(() => {
    if (userData?.userId) {
      const db = getDatabase();
      const userRef = ref(db, `users/${userData.userId}`);
      const unsubscribe = onValue(userRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data.customMessage !== undefined) {
            setUserData(prev => ({ 
              ...prev, 
              customMessage: data.customMessage 
            }));
          }
        }
      });
      return () => unsubscribe();
    }
  }, [userData?.userId]);

  // Listen for universal message from admin
  useEffect(() => {
    const db = getDatabase();
    const universalMessageRef = ref(db, 'admin/universalMessage');
    const unsubscribe = onValue(universalMessageRef, (snapshot) => {
      if (snapshot.exists()) {
        setUniversalMessage(snapshot.val());
      } else {
        setUniversalMessage('');
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch user orders
  useEffect(() => {
    if (!userData?.userId) return;

    const fetchUserOrders = async () => {
      setOrdersLoading(true);
      try {
        const db = getDatabase();
        const userId = userData.userId;
        
        const allOrdersSnapshot = await get(ref(db, 'allOrders'));
        let orders = [];
        
        if (allOrdersSnapshot.exists()) {
          const allOrdersData = allOrdersSnapshot.val();
          Object.keys(allOrdersData).forEach(key => {
            const order = allOrdersData[key];
            if (order.userId === userId) {
              orders.push({
                id: key,
                ...order
              });
            }
          });
        } else {
          const ordersSnapshot = await get(ref(db, `orders/${userId}`));
          if (ordersSnapshot.exists()) {
            const ordersData = ordersSnapshot.val();
            orders = Object.keys(ordersData).map(key => ({
              id: key,
              ...ordersData[key]
            }));
          }
        }
        
        orders.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        
        setUserOrders(orders);
        const total = orders.reduce((sum, order) => sum + (order.totalAmount || order.total || 0), 0);
        setOrdersTotal(total);
        
      } catch (error) {
        console.error('Error fetching user orders:', error);
      }
      setOrdersLoading(false);
    };

    fetchUserOrders();

    const db = getDatabase();
    const ordersRef = ref(db, `orders/${userData.userId}`);
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      if (snapshot.exists()) {
        const ordersData = snapshot.val();
        const orders = Object.keys(ordersData).map(key => ({
          id: key,
          ...ordersData[key]
        }));
        orders.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        setUserOrders(orders);
        const total = orders.reduce((sum, order) => sum + (order.totalAmount || order.total || 0), 0);
        setOrdersTotal(total);
      }
    });

    return () => unsubscribe();
  }, [userData?.userId]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    navigate('/');
    window.location.reload();
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditData({
      state: userData.state || '',
      city: userData.city || '',
      pin: userData.pin || '',
      address: userData.address || '',
      industryArea: userData.industryArea || '',
      experience: userData.experience || '',
      preferredLocation: userData.preferredLocation || ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      const db = getDatabase();
      await update(ref(db, `users/${user.userId}`), {
        state: editData.state,
        city: editData.city,
        pin: editData.pin,
        address: editData.address,
        industryArea: editData.industryArea,
        experience: editData.experience,
        preferredLocation: editData.preferredLocation
      });
      
      setUserData(prev => ({ ...prev, ...editData }));
      setEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    }
    setSaveLoading(false);
  };

  // NEW: Toggle orders dropdown
  const toggleOrders = () => {
    setOrdersExpanded(!ordersExpanded);
  };

  // NEW: Toggle individual order expansion
  const toggleOrderExpand = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const renderField = (label, value, name, type = 'text', placeholder = '') => {
    if (editing) {
      return React.createElement(
        'div',
        { className: 'profile-field-modern' },
        React.createElement('label', { className: 'field-label-modern' }, label),
        React.createElement('input', {
          type: type,
          name: name,
          value: editData[name] || '',
          onChange: handleChange,
          className: 'edit-input-modern',
          placeholder: placeholder || `Enter ${label.toLowerCase()}`
        })
      );
    }
    return React.createElement(
      'div',
      { className: 'profile-field-modern' },
      React.createElement('span', { className: 'field-label-modern' }, label),
      React.createElement('span', { className: 'field-value-modern' }, value || 'Not provided')
    );
  };

  const renderReadOnlyField = (label, value) => {
    return React.createElement(
      'div',
      { className: 'profile-field-modern read-only' },
      React.createElement('span', { className: 'field-label-modern' }, label),
      React.createElement('span', { className: 'field-value-modern' }, value || 'Not provided')
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': '#00b894',
      'success': '#00b894',
      'pending': '#fdcb6e',
      'failed': '#ff6b6b',
      'cancelled': '#dc3545',
      'processing': '#6c5ce7',
      'timeout': '#e17055',
      'error': '#e17055'
    };
    return colors[status?.toLowerCase()] || '#6b7280';
  };

  if (loading) {
    return React.createElement(
      'main',
      { className: 'page-content' },
      React.createElement(
        'div',
        { className: 'container', style: { textAlign: 'center', padding: '60px 0' } },
        React.createElement('i', { className: 'fas fa-spinner fa-spin', style: { fontSize: '48px', color: '#65d2bc' } }),
        React.createElement('p', null, 'Loading profile...')
      )
    );
  }

  if (error) {
    return React.createElement(
      'main',
      { className: 'page-content' },
      React.createElement(
        'div',
        { className: 'container' },
        React.createElement(
          'div',
          { className: 'error-message', style: { textAlign: 'center', padding: '40px' } },
          React.createElement('i', { className: 'fas fa-exclamation-circle', style: { fontSize: '48px', color: '#dc2626' } }),
          React.createElement('h3', null, error),
          React.createElement(
            'button',
            { className: 'btn-primary', onClick: () => handleNavigation('/') },
            'Go Home'
          )
        )
      )
    );
  }

  return React.createElement(
    'main',
    { className: 'page-content profile-page-modern' },
    React.createElement(
      'div',
      { className: 'container-modern' },
      
      // Profile Header with Gradient Background
      React.createElement(
        'div',
        { className: 'profile-header-modern' },
        React.createElement(
          'div',
          { className: 'profile-header-content' },
          React.createElement(
            'div',
            { className: 'profile-avatar-modern' },
            React.createElement(
              'span',
              { className: 'avatar-text-modern' },
              userData?.firstName ? userData.firstName.charAt(0).toUpperCase() : 'U'
            )
          ),
          React.createElement(
            'div',
            { className: 'profile-header-info' },
            React.createElement('h1', { className: 'profile-name-modern' }, 
              `${userData?.firstName || ''} ${userData?.lastName || ''}`
            ),
            React.createElement('p', { className: 'profile-email-modern' }, 
              React.createElement('i', { className: 'fas fa-envelope' }),
              ' ',
              userData?.email || 'No email provided'
            ),
            React.createElement(
              'div',
              { className: 'profile-badge' },
              React.createElement('span', { className: 'badge-active' }, 'Active Member')
            )
          )
        )
      ),

      // Universal Custom Message from Admin
      universalMessage ? React.createElement(
        'div',
        { 
          className: 'universal-message-banner',
          style: { 
            marginBottom: '20px',
            padding: '18px 25px',
            background: 'linear-gradient(135deg, #e8f0fe 0%, #d4e4f7 100%)',
            borderRadius: '12px',
            borderLeft: '4px solid #1a73e8',
            boxShadow: '0 2px 10px rgba(26, 115, 232, 0.15)',
            animation: 'slideIn 0.5s ease-out'
          } 
        },
        React.createElement(
          'div',
          { style: { display: 'flex', alignItems: 'flex-start', gap: '15px' } },
          React.createElement('i', { 
            className: 'fas fa-globe', 
            style: { fontSize: '22px', color: '#1a73e8', marginTop: '2px' } 
          }),
          React.createElement(
            'div',
            { style: { flex: 1 } },
            React.createElement(
              'p',
              { style: { margin: '0', color: '#1a2332', fontSize: '15px', lineHeight: '1.6' } },
              universalMessage
            )
          )
        )
      ) : null,

      // User-specific Custom Message from Admin
      userData?.customMessage ? React.createElement(
        'div',
        { 
          className: 'custom-message-banner',
          style: { 
            marginBottom: '20px',
            padding: '18px 25px',
            background: 'linear-gradient(135deg, #fef9e7 0%, #fdebd0 100%)',
            borderRadius: '12px',
            borderLeft: '4px solid #f39c12',
            boxShadow: '0 2px 10px rgba(243, 156, 18, 0.15)',
            animation: 'slideIn 0.5s ease-out'
          } 
        },
        React.createElement(
          'div',
          { style: { display: 'flex', alignItems: 'flex-start', gap: '15px' } },
          React.createElement('i', { 
            className: 'fas fa-user-tag', 
            style: { fontSize: '22px', color: '#f39c12', marginTop: '2px' } 
          }),
          React.createElement(
            'div',
            { style: { flex: 1 } },
            React.createElement(
              'span',
              { 
                style: { 
                  fontSize: '12px', 
                  fontWeight: '700', 
                  color: '#f39c12',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '4px'
                } 
              },
              '💬 Personal Message'
            ),
            React.createElement(
              'p',
              { style: { margin: '0', color: '#5d4e37', fontSize: '15px', lineHeight: '1.6' } },
              userData.customMessage
            )
          )
        )
      ) : null,

      // Career Information - Full Width Card
      React.createElement(
        'div',
        { className: 'career-full-width-card' },
        React.createElement(
          'div',
          { className: 'career-card-header' },
          React.createElement('i', { className: 'fas fa-building', style: { color: '#65d2bc', fontSize: '22px' } }),
          React.createElement('h3', null, 'Career Information')
        ),
        React.createElement(
          'div',
          { className: 'career-card-body' },
          React.createElement(
            'div',
            { className: 'career-fields-grid' },
            React.createElement(
              'div',
              { className: 'career-field-item' },
              React.createElement('span', { className: 'career-field-label' }, 'Interested Companies'),
              React.createElement('span', { className: 'career-field-value' }, 
                userData?.interestedCompanies || 'Not specified'
              )
            ),
            React.createElement(
              'div',
              { className: 'career-field-item' },
              React.createElement('span', { className: 'career-field-label' }, 'Expected CTC'),
              React.createElement('span', { className: 'career-field-value' }, 
                userData?.ctc || 'Not specified'
              )
            )
          )
        )
      ),

      // Profile Content Grid
      React.createElement(
        'div',
        { className: 'profile-grid-modern' },
        
        // Left Column - Personal & Location
        React.createElement(
          'div',
          { className: 'profile-left-column' },
          
          // Personal Information Card (Read-only)
          React.createElement(
            'div',
            { className: 'profile-card-modern' },
            React.createElement(
              'div',
              { className: 'card-header-modern' },
              React.createElement('i', { className: 'fas fa-user-circle', style: { color: '#65d2bc', fontSize: '20px' } }),
              React.createElement('h3', null, 'Personal Information')
            ),
            React.createElement(
              'div',
              { className: 'card-body-modern' },
              renderReadOnlyField('First Name', userData?.firstName),
              renderReadOnlyField('Last Name', userData?.lastName),
              renderReadOnlyField('Email Address', userData?.email),
              renderReadOnlyField('Phone Number', userData?.phone)
            )
          ),

          // Location Information Card (Editable)
          React.createElement(
            'div',
            { className: 'profile-card-modern' },
            React.createElement(
              'div',
              { className: 'card-header-modern' },
              React.createElement('i', { className: 'fas fa-map-pin', style: { color: '#65d2bc', fontSize: '20px' } }),
              React.createElement('h3', null, 'Location Details')
            ),
            React.createElement(
              'div',
              { className: 'card-body-modern' },
              renderField('State', userData?.state, 'state'),
              renderField('City', userData?.city, 'city'),
              renderField('Postal Code', userData?.pin, 'pin'),
              renderField('Address', userData?.address, 'address')
            )
          )
        ),

        // Right Column - Professional Details
        React.createElement(
          'div',
          { className: 'profile-right-column' },
          
          // Professional Information Card (Editable)
          React.createElement(
            'div',
            { className: 'profile-card-modern' },
            React.createElement(
              'div',
              { className: 'card-header-modern' },
              React.createElement('i', { className: 'fas fa-briefcase', style: { color: '#65d2bc', fontSize: '20px' } }),
              React.createElement('h3', null, 'Professional Details')
            ),
            React.createElement(
              'div',
              { className: 'card-body-modern' },
              renderField('Industry', userData?.industryArea, 'industryArea'),
              renderField('Experience', userData?.experience, 'experience', 'text', 'e.g., 5 years'),
              renderField('Preferred Location', userData?.preferredLocation, 'preferredLocation')
            )
          )
        )
      ),

      // NEW: My Orders - Dropdown/Accordion Section at Bottom
      React.createElement(
        'div',
        { 
          className: 'orders-dropdown-section',
          style: { 
            marginTop: '30px',
            background: 'white',
            borderRadius: '16px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            overflow: 'hidden',
            border: '2px solid #65d2bc'
          }
        },
        // Dropdown Header
        React.createElement(
          'div',
          { 
            className: 'orders-dropdown-header',
            onClick: toggleOrders,
            style: { 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px 24px',
              background: ordersExpanded ? '#f0fdf4' : '#f8fafc',
              borderBottom: ordersExpanded ? '2px solid #65d2bc' : '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              userSelect: 'none'
            },
            onMouseEnter: (e) => {
              e.currentTarget.style.background = ordersExpanded ? '#e8f5e9' : '#f1f5f9';
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.background = ordersExpanded ? '#f0fdf4' : '#f8fafc';
            }
          },
          React.createElement(
            'div',
            { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
            React.createElement('i', { 
              className: 'fas fa-shopping-bag', 
              style: { color: '#65d2bc', fontSize: '22px' } 
            }),
            React.createElement('h3', { style: { margin: 0, color: '#0f172a' } }, 'My Orders'),
            React.createElement(
              'span',
              { 
                style: { 
                  background: '#65d2bc', 
                  color: 'white', 
                  padding: '2px 12px', 
                  borderRadius: '12px', 
                  fontSize: '13px', 
                  fontWeight: '600' 
                } 
              },
              userOrders.length
            ),
            !ordersLoading && userOrders.length > 0 && React.createElement(
              'span',
              { style: { fontSize: '13px', color: '#64748b' } },
              `(Total: ₹${ordersTotal.toLocaleString()})`
            )
          ),
          React.createElement(
            'div',
            { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
            ordersLoading && React.createElement(
              'i',
              { className: 'fas fa-spinner fa-spin', style: { color: '#65d2bc' } }
            ),
            React.createElement('i', { 
              className: `fas fa-chevron-${ordersExpanded ? 'up' : 'down'}`,
              style: { 
                color: '#64748b', 
                fontSize: '18px',
                transition: 'transform 0.3s ease'
              } 
            })
          )
        ),
        
        // Dropdown Body
        ordersExpanded && React.createElement(
          'div',
          { 
            className: 'orders-dropdown-body',
            style: { 
              padding: '20px 24px',
              animation: 'slideDown 0.3s ease'
            }
          },
          ordersLoading ? 
            React.createElement(
              'div',
              { style: { textAlign: 'center', padding: '30px' } },
              React.createElement('i', { className: 'fas fa-spinner fa-spin', style: { color: '#65d2bc', fontSize: '30px' } }),
              React.createElement('p', { style: { marginTop: '10px', color: '#64748b' } }, 'Loading orders...')
            ) :
            userOrders.length === 0 ?
              React.createElement(
                'div',
                { style: { textAlign: 'center', padding: '30px', color: '#94a3b8' } },
                React.createElement('i', { className: 'fas fa-inbox', style: { fontSize: '40px', display: 'block', marginBottom: '10px' } }),
                'No orders yet. Start shopping!'
              ) :
              React.createElement(
                'div',
                { style: { display: 'flex', flexDirection: 'column', gap: '12px' } },
                userOrders.map((order) => {
                  const isExpanded = expandedOrderId === order.id;
                  return React.createElement(
                    'div',
                    { 
                      key: order.id, 
                      className: 'order-accordion-item',
                      style: { 
                        border: isExpanded ? '2px solid #65d2bc' : '1px solid #e2e8f0',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        background: isExpanded ? '#fafffe' : 'white'
                      }
                    },
                    // Order Summary (always visible)
                    React.createElement(
                      'div',
                      { 
                        className: 'order-summary-row',
                        onClick: () => toggleOrderExpand(order.id),
                        style: { 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          padding: '12px 16px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: isExpanded ? '#f0fdf4' : 'white'
                        },
                        onMouseEnter: (e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.background = '#f8fafc';
                          }
                        },
                        onMouseLeave: (e) => {
                          if (!isExpanded) {
                            e.currentTarget.style.background = 'white';
                          }
                        }
                      },
                      React.createElement(
                        'div',
                        { style: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' } },
                        React.createElement(
                          'span',
                          { style: { fontWeight: '700', color: '#0f172a', fontSize: '14px' } },
                          `#${order.id?.slice(-8) || 'N/A'}`
                        ),
                        React.createElement(
                          'span',
                          { 
                            style: { 
                              background: getStatusColor(order.status),
                              color: 'white',
                              padding: '2px 10px',
                              borderRadius: '20px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'capitalize'
                            } 
                          },
                          order.status || 'Pending'
                        ),
                        React.createElement(
                          'span',
                          { style: { color: '#64748b', fontSize: '13px' } },
                          formatDate(order.createdAt)
                        )
                      ),
                      React.createElement(
                        'div',
                        { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
                        React.createElement(
                          'span',
                          { style: { fontWeight: '700', color: '#65d2bc', fontSize: '15px' } },
                          `₹${(order.totalAmount || order.total || 0).toLocaleString()}`
                        ),
                        React.createElement('i', { 
                          className: `fas fa-chevron-${isExpanded ? 'up' : 'down'}`,
                          style: { color: '#94a3b8', fontSize: '14px' } 
                        })
                      )
                    ),
                    // Order Details (expandable)
                    isExpanded && React.createElement(
                      'div',
                      { 
                        className: 'order-details-expanded',
                        style: { 
                          padding: '16px',
                          borderTop: '1px solid #e2e8f0',
                          background: '#fafbfc',
                          animation: 'slideDown 0.3s ease'
                        }
                      },
                      // Items
                      React.createElement(
                        'div',
                        { style: { marginBottom: '12px' } },
                        React.createElement(
                          'h5',
                          { style: { margin: '0 0 8px 0', color: '#0f172a', fontSize: '14px', fontWeight: '600' } },
                          'Items Purchased'
                        ),
                        React.createElement(
                          'div',
                          { style: { display: 'flex', flexDirection: 'column', gap: '4px' } },
                          order.items && order.items.length > 0 ?
                            order.items.map((item, index) =>
                              React.createElement(
                                'div',
                                { 
                                  key: index, 
                                  style: { 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    padding: '6px 12px',
                                    background: 'white',
                                    borderRadius: '6px',
                                    border: '1px solid #f1f5f9',
                                    fontSize: '14px'
                                  } 
                                },
                                React.createElement('span', { style: { color: '#1e293b' } }, 
                                  `${index + 1}. ${item.name || `Item ${index + 1}`}`
                                ),
                                React.createElement('span', { style: { fontWeight: '500', color: '#0f172a' } }, 
                                  `₹${(item.price || 0).toLocaleString()}`
                                )
                              )
                            ) :
                            React.createElement('span', { style: { color: '#94a3b8', fontSize: '13px' } }, 'No item details')
                        )
                      ),
                      // Order details
                      React.createElement(
                        'div',
                        { 
                          style: { 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: '8px',
                            padding: '12px',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #f1f5f9'
                          } 
                        },
                        React.createElement(
                          'div',
                          null,
                          React.createElement('span', { style: { color: '#64748b', fontSize: '12px', display: 'block' } }, 'Order ID'),
                          React.createElement('span', { style: { color: '#0f172a', fontSize: '13px', fontWeight: '500' } }, order.id)
                        ),
                        order.transactionId && React.createElement(
                          'div',
                          null,
                          React.createElement('span', { style: { color: '#64748b', fontSize: '12px', display: 'block' } }, 'Transaction ID'),
                          React.createElement('span', { style: { color: '#0f172a', fontSize: '13px', fontWeight: '500' } }, order.transactionId)
                        ),
                        order.utr && React.createElement(
                          'div',
                          null,
                          React.createElement('span', { style: { color: '#64748b', fontSize: '12px', display: 'block' } }, 'UTR Number'),
                          React.createElement('span', { style: { color: '#0f172a', fontSize: '13px', fontWeight: '500' } }, order.utr)
                        ),
                        order.paymentMode && React.createElement(
                          'div',
                          null,
                          React.createElement('span', { style: { color: '#64748b', fontSize: '12px', display: 'block' } }, 'Payment Mode'),
                          React.createElement('span', { style: { color: '#0f172a', fontSize: '13px', fontWeight: '500' } }, order.paymentMode)
                        ),
                        order.referralCode && React.createElement(
                          'div',
                          null,
                          React.createElement('span', { style: { color: '#64748b', fontSize: '12px', display: 'block' } }, 'Referral Code'),
                          React.createElement('span', { style: { color: '#0f172a', fontSize: '13px', fontWeight: '500' } }, 
                            order.referralIsUniversal ? '🌐 Universal' : order.referralCode
                          )
                        ),
                        React.createElement(
                          'div',
                          null,
                          React.createElement('span', { style: { color: '#64748b', fontSize: '12px', display: 'block' } }, 'Status'),
                          React.createElement(
                            'span',
                            { 
                              style: { 
                                background: getStatusColor(order.status),
                                color: 'white',
                                padding: '2px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'capitalize',
                                display: 'inline-block'
                              } 
                            },
                            order.status || 'Pending'
                          )
                        ),
                        React.createElement(
                          'div',
                          { style: { gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '4px' } },
                          React.createElement(
                            'div',
                            { style: { display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '15px' } },
                            React.createElement('span', null, 'Total Amount'),
                            React.createElement('span', { style: { color: '#65d2bc' } }, 
                              `₹${(order.totalAmount || order.total || 0).toLocaleString()}`
                            )
                          )
                        )
                      )
                    )
                  );
                })
              )
        )
      ),

      // Action Buttons
      React.createElement(
        'div',
        { className: 'profile-actions-modern' },
        !editing ? React.createElement(
          'button',
          { 
            className: 'btn-edit-profile-modern',
            onClick: handleEdit
          },
          React.createElement('i', { className: 'fas fa-pen' }),
          ' Edit Profile'
        ) : React.createElement(
          React.Fragment,
          null,
          React.createElement(
            'button',
            { 
              className: 'btn-save-profile-modern',
              onClick: handleSave,
              disabled: saveLoading
            },
            saveLoading ? 'Saving...' : React.createElement(React.Fragment, null, React.createElement('i', { className: 'fas fa-check' }), ' Save Changes')
          ),
          React.createElement(
            'button',
            { 
              className: 'btn-cancel-profile-modern',
              onClick: handleCancel
            },
            React.createElement('i', { className: 'fas fa-times' }),
            ' Cancel'
          )
        ),
        React.createElement(
          'button',
          { 
            className: 'btn-cart-modern',
            onClick: () => handleNavigation('/cart')
          },
          React.createElement('i', { className: 'fas fa-shopping-bag' }),
          ' View Cart'
        ),
        React.createElement(
          'button',
          { 
            className: 'btn-logout-modern',
            onClick: handleLogout
          },
          React.createElement('i', { className: 'fas fa-sign-out-alt' }),
          ' Logout'
        ),
        React.createElement(
          'button',
          { 
            className: 'btn-home-modern',
            onClick: () => handleNavigation('/')
          },
          React.createElement('i', { className: 'fas fa-home' }),
          ' Home'
        )
      )
    )
  );
};

export default Profile;